import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Users, AlertTriangle, ShieldCheck, Flame, ArrowUpRight, TrendingUp, Store, ArrowRight } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { listEmployees } from "../api/employees";
import { listComplaints } from "../api/complaints";
import { listBranches } from "../api/branches";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { Card, StatCard, Skeleton, Button, EmptyState, Avatar } from "../components/ui";
import { StatusChip, PriorityChip } from "../components/Chips";

const STATUS_ORDER = ["OPEN", "ASSIGNED", "UNDER_REVIEW", "HOLD", "RESOLVED", "REOPENED"];

// Updated Donut buckets with full labels
const DONUT_BUCKETS = [
  { key: "open", label: "Open", statuses: ["OPEN"], color: "#6b70b0" },
  { key: "assigned", label: "Assigned", statuses: ["ASSIGNED"], color: "#f2b049" },
  { key: "underReview", label: "Under Review", statuses: ["UNDER_REVIEW"], color: "#8b5cf6" },
  { key: "hold", label: "On Hold", statuses: ["HOLD"], color: "#f59e0b" },
  { key: "resolved", label: "Resolved", statuses: ["RESOLVED"], color: "#059669" },
  { key: "reopened", label: "Rejected", statuses: ["REOPENED"], color: "#ef4444" },
];

export default function Dashboard() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";

  // The two tiers see genuinely different things: a chain works across its
  // branches (which it onboards) and gets complaints aggregated over all of
  // them, while a branch works within itself.
  return isChain ? <ChainDashboard partner={partner} /> : <BranchDashboard partner={partner} />;
}

/* -------------------------------------------------------------------- */
/* CHAIN — branches, staff across every branch, aggregated complaints    */
/* -------------------------------------------------------------------- */

function ChainDashboard({ partner }) {
  const [loading, setLoading] = useState(true);
  const [employeeTotal, setEmployeeTotal] = useState(0);
  const [activeEmployeeTotal, setActiveEmployeeTotal] = useState(0);
  const [branchTotal, setBranchTotal] = useState(0);
  const [activeBranchTotal, setActiveBranchTotal] = useState(0);
  const [recentBranches, setRecentBranches] = useState([]);
  const [complaintTotal, setComplaintTotal] = useState(0);
  const [urgentTotal, setUrgentTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        // Totals come from `pagination.total` on a 1-item page, so each count
        // is a cheap query rather than a full list the client has to tally.
        const [
          empAll,
          empActive,
          branchesAll,
          branchesActive,
          branchSample,
          complaintsAll,
          complaintsUrgent,
          ...byStatus
        ] = await Promise.all([
          listEmployees({ limit: 1 }),
          listEmployees({ limit: 1, status: "ACTIVE" }),
          listBranches({ limit: 1 }),
          listBranches({ limit: 1, status: "ACTIVE" }),
          listBranches({ limit: 6 }),
          listComplaints({ limit: 1 }),
          listComplaints({ limit: 1, priority: "URGENT" }),
          ...STATUS_ORDER.map((status) => listComplaints({ limit: 1, status })),
        ]);
        if (!mounted) return;
        setEmployeeTotal(empAll.data.pagination.total);
        setActiveEmployeeTotal(empActive.data.pagination.total);
        setBranchTotal(branchesAll.data.pagination.total);
        setActiveBranchTotal(branchesActive.data.pagination.total);
        setRecentBranches(branchSample.data.items);
        setComplaintTotal(complaintsAll.data.pagination.total);
        setUrgentTotal(complaintsUrgent.data.pagination.total);
        setStatusCounts(
          Object.fromEntries(
            STATUS_ORDER.map((status, i) => [status, byStatus[i].data.pagination.total])
          )
        );
      } catch {
        // Non-blocking: dashboard degrades gracefully with zeroed stats.
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        <PageHeader
          title="Welcome back"
          subtitle={partner ? `${partner?.name} \u00b7 ${partner?.partnerId}` : "Here's an overview of your chain."}
          actions={
            <Link to="/branches">
              <Button
                variant="secondary"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 transition-all duration-200 text-sm sm:text-base px-3 sm:px-4 py-1.5 sm:py-2"
              >
                Manage branches <ArrowUpRight size={16} className="hidden sm:inline" />
              </Button>
            </Link>
          }
        />

        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="p-3 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100">
                <Skeleton className="h-3 sm:h-4 w-20 sm:w-24" />
                <Skeleton className="mt-2 sm:mt-3 h-6 sm:h-8 w-12 sm:w-16" />
              </Card>
            ))
          ) : (
            <>
              <StatCard
                label="Total branches"
                value={branchTotal}
                icon={Store}
                tone="secondary"
                className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-md hover:shadow-lg transition-shadow duration-200 p-3 sm:p-5"
              />
              <StatCard
                label="Active branches"
                value={activeBranchTotal}
                icon={ShieldCheck}
                tone="green"
                className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-md hover:shadow-lg transition-shadow duration-200 p-3 sm:p-5"
              />
              <StatCard
                label="Total employees"
                value={employeeTotal}
                icon={Users}
                tone="blue"
                className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-md hover:shadow-lg transition-shadow duration-200 p-3 sm:p-5"
              />
              <StatCard
                label="Active employees"
                value={activeEmployeeTotal}
                icon={ShieldCheck}
                tone="primary"
                className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-md hover:shadow-lg transition-shadow duration-200 p-3 sm:p-5"
              />
            </>
          )}
        </div>

        <p className="mt-4 sm:mt-5 text-xs sm:text-body-sm text-gray-500">
          Employee counts cover every branch plus your head office. Complaints below are aggregated
          across all of your outlets.
        </p>

        {/* Complaints, aggregated across every branch — the chain-level view
            the branches themselves can't see. */}
        <Card className="mt-4 sm:mt-5 p-4 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h2 className="text-base sm:text-headline-sm text-gray-800 font-semibold">
                Complaints across your branches
              </h2>
              <p className="text-xs sm:text-body-sm text-gray-500">
                {loading
                  ? "Loading…"
                  : complaintTotal === 0
                    ? "Nothing raised against any of your outlets yet."
                    : `${complaintTotal} total${urgentTotal > 0 ? ` · ${urgentTotal} urgent` : ""}`}
              </p>
            </div>
            <Link
              to="/complaints"
              className="text-xs sm:text-body-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors duration-200 whitespace-nowrap"
            >
              View all
            </Link>
          </div>

          <div className="mt-3 sm:mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16" />)
              : STATUS_ORDER.map((status) => (
                  <Link
                    key={status}
                    to={`/complaints?status=${status}`}
                    className="rounded-lg border border-emerald-100 bg-emerald-50/40 px-3 py-2.5 transition-all duration-200 hover:border-emerald-300 hover:bg-emerald-50"
                  >
                    <p className="text-lg sm:text-headline-sm font-semibold text-gray-800">
                      {statusCounts[status] ?? 0}
                    </p>
                    <p className="mt-0.5 text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500">
                      {status.replace("_", " ")}
                    </p>
                  </Link>
                ))}
          </div>
        </Card>

        {/* Recent Branches */}
        <Card className="mt-4 sm:mt-5 p-4 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h2 className="text-base sm:text-headline-sm text-gray-800 font-semibold">Your branches</h2>
              <p className="text-xs sm:text-body-sm text-gray-500">Recently onboarded outlets.</p>
            </div>
            <Link
              to="/branches"
              className="text-xs sm:text-body-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors duration-200 whitespace-nowrap"
            >
              View all
            </Link>
          </div>

          <div className="mt-3 sm:mt-4 divide-y divide-emerald-100/50">
            {loading &&
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 sm:gap-4 py-3 sm:py-3.5">
                  <Skeleton className="h-4 w-full" />
                </div>
              ))}
            {!loading && recentBranches.length === 0 && (
              <div className="py-8">
                <EmptyState
                  icon={Store}
                  title="No branches yet"
                  description="Onboard your first outlet to get started."
                  action={
                    <Link to="/branches">
                      <Button className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25">
                        Onboard branch <ArrowRight size={16} />
                      </Button>
                    </Link>
                  }
                />
              </div>
            )}
            {!loading &&
              recentBranches.map((b) => (
                <Link
                  key={b._id}
                  to={`/branches/${b._id}`}
                  className="flex items-center gap-3 sm:gap-4 py-3 sm:py-3.5 group hover:bg-emerald-50/50 px-2 sm:px-3 -mx-2 sm:-mx-3 rounded-lg transition-all duration-200"
                >
                  <Avatar name={b.name} size={32} className="bg-gradient-to-br from-emerald-400 to-green-500 text-white text-xs shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm sm:text-body-sm font-medium text-gray-800 group-hover:text-emerald-700 transition-colors duration-200 line-clamp-1">
                      {b.name}
                    </p>
                    <p className="text-xs sm:text-body-sm text-gray-500 truncate">
                      {b.partnerId}
                      {b.location?.city ? ` · ${b.location.city}` : ""}
                    </p>
                  </div>
                  <StatusChip status={b.status} />
                </Link>
              ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* BRANCH — employees + own complaints                                  */
/* -------------------------------------------------------------------- */

function BranchDashboard({ partner }) {
  const [loading, setLoading] = useState(true);
  const [employeeTotal, setEmployeeTotal] = useState(0);
  const [activeEmployeeTotal, setActiveEmployeeTotal] = useState(0);
  const [complaintTotal, setComplaintTotal] = useState(0);
  const [urgentTotal, setUrgentTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [recentComplaints, setRecentComplaints] = useState([]);
  const [trendSource, setTrendSource] = useState([]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const [empAll, empActive, complaintsAll, urgent, sample, ...statusResults] =
          await Promise.all([
            listEmployees({ limit: 1 }),
            listEmployees({ limit: 1, status: "ACTIVE" }),
            listComplaints({ limit: 1 }),
            listComplaints({ limit: 1, priority: "URGENT" }),
            listComplaints({ limit: 100 }),
            ...STATUS_ORDER.map((status) => listComplaints({ limit: 1, status })),
          ]);
        if (!mounted) return;
        setEmployeeTotal(empAll.data.pagination.total);
        setActiveEmployeeTotal(empActive.data.pagination.total);
        setComplaintTotal(complaintsAll.data.pagination.total);
        setUrgentTotal(urgent.data.pagination.total);
        setRecentComplaints(sample.data.items.slice(0, 6));
        setTrendSource(sample.data.items);

        const counts = {};
        STATUS_ORDER.forEach((status, i) => {
          counts[status] = statusResults[i].data.pagination.total;
        });
        setStatusCounts(counts);
      } catch {
        // Non-blocking: dashboard degrades gracefully with zeroed stats.
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  // Updated Donut data with full labels
  const donutData = useMemo(
    () =>
      DONUT_BUCKETS.map((b) => ({
        name: b.label,
        value: b.statuses.reduce((sum, s) => sum + (statusCounts[s] || 0), 0),
        color: b.color,
      })).filter((d) => d.value > 0),
    [statusCounts]
  );
  const donutTotal = donutData.reduce((s, d) => s + d.value, 0);

  const trendData = useMemo(() => {
    const days = 14;
    const buckets = new Map();
    const today = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      buckets.set(key, { date: key, count: 0 });
    }
    trendSource.forEach((c) => {
      const key = (c.createdAt || c.updatedAt || "").slice(0, 10);
      if (buckets.has(key)) buckets.get(key).count += 1;
    });
    return Array.from(buckets.values()).map((b) => ({
      ...b,
      label: new Date(b.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    }));
  }, [trendSource]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        <PageHeader
          title="Welcome back"
          subtitle={
            partner
              ? `${partner?.name} \u00b7 ${partner?.partnerId}`
              : "Here's what's happening across your organization."
          }
          actions={
            <Link to="/complaints">
              <Button 
                variant="secondary"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 transition-all duration-200 text-sm sm:text-base px-3 sm:px-4 py-1.5 sm:py-2"
              >
                View complaints <ArrowUpRight size={16} className="hidden sm:inline" />
              </Button>
            </Link>
          }
        />

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 mt-4 sm:mt-6">
          {/* Area Chart */}
          <Card className="p-4 sm:p-5 lg:col-span-2 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h2 className="text-base sm:text-headline-sm text-gray-800 font-semibold">Complaints raised</h2>
                <p className="text-xs sm:text-body-sm text-gray-500">Last 14 days</p>
              </div>
              <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 sm:px-2.5 py-0.5 sm:py-1 text-xs sm:text-label-sm font-semibold text-emerald-700 whitespace-nowrap">
                <TrendingUp size={13} className="hidden sm:block" /> {trendSource.length} tracked
              </span>
            </div>
            <div className="mt-3 sm:mt-4 h-48 sm:h-56 md:h-64">
              {loading ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData} margin={{ left: -20, right: 10, top: 5, bottom: 5 }}>
                    <defs>
                      <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke="#e5eeff" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: "#6b7280" }}
                      axisLine={{ stroke: "#e5eeff" }}
                      tickLine={false}
                      interval={2}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 10, fill: "#6b7280" }}
                      axisLine={false}
                      tickLine={false}
                      width={28}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "1px solid #d1fae5",
                        fontSize: 12,
                        backgroundColor: "white",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="#059669"
                      strokeWidth={2.5}
                      fill="url(#trendFill)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>

          {/* Donut Chart */}
          <Card className="p-4 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
            <h2 className="text-base sm:text-headline-sm text-gray-800 font-semibold">Complaint mix</h2>
            <p className="text-xs sm:text-body-sm text-gray-500">By lifecycle stage</p>
            <div className="relative mt-2 h-36 sm:h-40 md:h-44">
              {loading ? (
                <Skeleton className="h-full w-full rounded-full" />
              ) : donutTotal === 0 ? (
                <div className="flex h-full items-center justify-center text-xs sm:text-body-sm text-gray-500">
                  No complaints yet
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={donutData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={40}
                        outerRadius={56}
                        paddingAngle={3}
                        stroke="none"
                      >
                        {donutData.map((d) => (
                          <Cell key={d.name} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          borderRadius: 8,
                          fontSize: 12,
                          backgroundColor: "white",
                          border: "1px solid #d1fae5",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-lg sm:text-headline-md text-gray-800 font-bold">{donutTotal}</span>
                    <span className="text-[10px] sm:text-body-sm text-gray-500">total</span>
                  </div>
                </>
              )}
            </div>
            {/* Donut Legend - shows full status names */}
            {!loading && donutData.length > 0 && (
              <ul className="mt-2 sm:mt-3 space-y-1.5 sm:space-y-2">
                {donutData.map((d) => (
                  <li key={d.name} className="flex items-center justify-between text-xs sm:text-body-sm">
                    <span className="flex items-center gap-1.5 sm:gap-2 text-gray-600">
                      <span className="h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full" style={{ background: d.color }} />
                      <span>{d.name}</span>
                    </span>
                    <span className="font-medium text-gray-800">{d.value}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Stat Cards */}
        <div className="mt-4 sm:mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="p-3 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100">
                <Skeleton className="h-3 sm:h-4 w-20 sm:w-24" />
                <Skeleton className="mt-2 sm:mt-3 h-6 sm:h-8 w-12 sm:w-16" />
              </Card>
            ))
          ) : (
            <>
              <StatCard 
                label="Total employees" 
                value={employeeTotal} 
                icon={Users} 
                tone="blue"
                className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-md hover:shadow-lg transition-shadow duration-200 p-3 sm:p-5"
              />
              <StatCard 
                label="Active employees" 
                value={activeEmployeeTotal} 
                icon={ShieldCheck} 
                tone="green"
                className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-md hover:shadow-lg transition-shadow duration-200 p-3 sm:p-5"
              />
              <StatCard 
                label="Total complaints" 
                value={complaintTotal} 
                icon={AlertTriangle} 
                tone="primary"
                className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-md hover:shadow-lg transition-shadow duration-200 p-3 sm:p-5"
              />
              <StatCard 
                label="Urgent complaints" 
                value={urgentTotal} 
                icon={Flame} 
                tone="orange"
                className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-md hover:shadow-lg transition-shadow duration-200 p-3 sm:p-5"
              />
            </>
          )}
        </div>

        {/* Recent Complaints */}
        <Card className="mt-4 sm:mt-5 p-4 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h2 className="text-base sm:text-headline-sm text-gray-800 font-semibold">Recent complaints</h2>
              <p className="text-xs sm:text-body-sm text-gray-500">Latest activity across your organization.</p>
            </div>
            <Link 
              to="/complaints" 
              className="text-xs sm:text-body-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors duration-200 whitespace-nowrap"
            >
              View all
            </Link>
          </div>

          <div className="mt-3 sm:mt-4 divide-y divide-emerald-100/50">
            {loading &&
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 sm:gap-4 py-3 sm:py-3.5">
                  <Skeleton className="h-4 w-full" />
                </div>
              ))}
            {!loading && recentComplaints.length === 0 && (
              <p className="py-6 sm:py-8 text-center text-xs sm:text-body-sm text-gray-500">
                No complaints yet — nice and quiet.
              </p>
            )}
            {!loading &&
              recentComplaints.map((c) => (
                <Link
                  key={c._id}
                  to={`/complaints/${c._id}`}
                  className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 sm:py-3.5 group hover:bg-emerald-50/50 px-2 sm:px-3 -mx-2 sm:-mx-3 rounded-lg transition-all duration-200"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm sm:text-body-sm font-medium text-gray-800 group-hover:text-emerald-700 transition-colors duration-200 line-clamp-1">
                      {c.title}
                    </p>
                    <p className="text-xs sm:text-body-sm text-gray-500 truncate">
                      {c.complaintId} · {c.assignee?.name ?? "Unassigned"}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5 sm:gap-2 flex-wrap">
                    <PriorityChip priority={c.priority} />
                    <StatusChip status={c.status} />
                  </div>
                </Link>
              ))}
          </div>
        </Card>
      </div>
    </div>
  );
}